import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router";

import { Shell } from "./components/Shell.js";
import { useResource } from "./hooks.js";
import { InvitationPage } from "./pages/InvitationPage.js";
import { KeysPage } from "./pages/KeysPage.js";
import { LoginPage } from "./pages/LoginPage.js";
import { MembersPage } from "./pages/MembersPage.js";
import { type RoutePath } from "./routes.js";
import { type Session } from "./types.js";

const LINKS: { label: string; to: RoutePath }[] = [
  { label: "Keys", to: "/keys" },
  { label: "Members", to: "/members" },
];

export function App() {
  const session = useResource<Session>("/app/studio/session");
  const [params, setParams] = useSearchParams();
  const [organization, setOrganization] = useState<string>();

  const memberships = session.value?.memberships || [];
  const requested = params.get("organization");

  useEffect(() => {
    if (memberships.length === 0) return;
    const match = memberships.find(
      (member) => member.organization === requested,
    );
    setOrganization((match || memberships[0]).organization);
  }, [requested, session.value]);

  if (session.loading) {
    return <p className="p-8 text-sm text-ink/50">Loading</p>;
  }
  if (session.error?.status === 401) return <LoginPage />;
  if (session.error?.status === 403) return <InvitationPage />;
  if (session.error) {
    return <p className="p-8 text-sm text-red-700">{session.error.message}</p>;
  }
  if (!session.value) return <LoginPage />;
  if (memberships.length === 0 || !organization) return <InvitationPage />;

  const membership = memberships.find(
    (member) => member.organization === organization,
  );

  return (
    <Shell title="Jaypie Studio" email={session.value.user.email} links={LINKS}>
      {memberships.length > 1 ? (
        <label className="mb-6 flex items-center gap-3 text-sm">
          <span className="text-ink/60">Organization</span>
          <select
            value={organization}
            onChange={(event) =>
              setParams({ organization: event.target.value })
            }
            className="rounded border border-black/15 px-3 py-1"
          >
            {memberships.map((member) => (
              <option key={member.organization} value={member.organization}>
                {member.organization}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <Routes>
        <Route path="/" element={<Navigate to="/keys" replace />} />
        <Route
          path="/keys"
          element={<KeysPage organization={organization} />}
        />
        <Route
          path="/members"
          element={
            <MembersPage membership={membership} organization={organization} />
          }
        />
        <Route path="/login" element={<Navigate to="/keys" replace />} />
      </Routes>
    </Shell>
  );
}
